import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "./Navbar";
import { useActiveOrderSelector, useUISelector, useUserSelector } from "../store/useSelectors";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { useActiveOrderActions, useUIActions } from "../store/useActions";
import { coreGetUserActiveOrder } from "../api/core";
import { toast } from "react-toastify";
import { driverLocationIcon } from "../static/styledIcons/styledIcons";
import { routingCreateControl } from "../services/routing";
import { useMap } from 'react-leaflet/hooks';
import Loader from "./Loader";
import { createLogger } from "../utils/logger";

const log = createLogger("active-order");

// Коды приходят с сервера (dispatcher.Order.status) — держим в синхроне с ним.
// Без 106/107/108/110 строка статуса на экране оставалась пустой.
const statusMap = {
    100: "Заказ создан",
    101: "Ожидается принятие заказа одним из водителей",
    102: "Водитель уже в пути",
    106: "Водитель подъезжает",
    107: "Водитель на месте",
    103: "Водитель ожидает вас",
    104: "Поездка началась, приятного пути!",
    108: "Водитель забронировал ваш заказ следующим",
    110: "Предварительный заказ принят",
    403: "Ожидается оплата поездки",
    105: "Поездка завершена",
}

// Неизвестный статус лучше показать нейтральным текстом, чем пустой строкой.
const STATUS_FALLBACK = "Заказ в работе";

/**
 * Приводит значение к строке для показа. Марка машины на бэкенде — объект (CarBrand),
 * поэтому прямой рендер давал бы "[object Object]".
 */
const textOf = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value);
    if (typeof value === "object") return String(value.name || value.title || "").trim();
    return "";
};

const ActiveOrderRouting = (props) => {
    const map = useMap();
    const [routing, setRouting] = useState();
    const { activeOrderData } = useActiveOrderSelector();

    useEffect(() => {
        drawRoute();
    }, [activeOrderData])

    const drawRoute = () => {
        if (!map || !activeOrderData) return;

        const start = activeOrderData?.driver?.location;
        if (!start || !start.latitude || !start.longitude) return;

        let end = null;
        let newRouting = null;

        switch (activeOrderData.status) {
            case 102:
                end = activeOrderData.start_location
                break
            case 104:
                end = activeOrderData.end_location
                break
        }
        if (end && end.latitude && end.longitude) {
            if (routing) routing.spliceWaypoints(0, 2);
            newRouting = routingCreateControl(start, end).addTo(map);
        }

        setRouting(newRouting);
    };

    return null;
}

const ActiveOrderMenu = (props) => {
    const [map, setMap] = useState();
    const [zoom, setZoom] = useState(13);
    const [travelTime, setTravelTime] = useState(null);
    // Фото водителя может не загрузиться — тогда рисуем инициал вместо битой картинки
    const [photoFailed, setPhotoFailed] = useState(false);
    // Последнее залогированное состояние заказа (чтобы не спамить логом при поллинге)
    const lastSignature = useRef(null);
    const { activeOrderData } = useActiveOrderSelector();
    const { setActiveOrderData } = useActiveOrderActions();
    const { centerPosition, activeOrderNavbarOpened } = useUISelector();
    const { setActiveOrderNavbarOpened } = useUIActions();
    const { token: userAuthToken } = useUserSelector();

    const driverInfo = activeOrderData?.driver?.driver;
    const driverLocation = (activeOrderData?.driver?.location
        && activeOrderData.driver.location.latitude != null
        && activeOrderData.driver.location.longitude != null
        && !isNaN(activeOrderData.driver.location.latitude)
        && !isNaN(activeOrderData.driver.location.longitude))
        ? [activeOrderData.driver.location.latitude, activeOrderData.driver.location.longitude]
        : null;

    // Функция для расчета времени поездки
    const calculateTravelTime = async (start, end) => {
        try {
            const response = await fetch(
                `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=false`
            );
            const data = await response.json();

            if (data.routes && data.routes[0]) {
                const duration = data.routes[0].duration; // в секундах
                setTravelTime(Math.ceil(duration / 60)); // переводим в минуты
            }
        } catch (error) {
            log.warn("Не удалось рассчитать время подачи", error);
        }
    };

    const updateActiveOrderData = () => {
        coreGetUserActiveOrder(userAuthToken)
            .then(response => {
                const data = response.data;

                // Заказ опрашивается раз в 5 сек — подробно пишем только смену состояния,
                // иначе лог заполняется одинаковыми строками.
                const signature = [
                    data?.status,
                    !!data?.driver,
                    !!data?.driver?.driver,
                    !!data?.driver?.driver?.photo_url,
                    !!data?.driver?.driver?.car,
                ].join("|");

                if (signature !== lastSignature.current) {
                    lastSignature.current = signature;
                    log.info("Активный заказ обновлён", {
                        status: data?.status,
                        statusText: statusMap[data?.status] || STATUS_FALLBACK,
                        hasDriver: !!data?.driver,
                        hasDriverInfo: !!data?.driver?.driver,
                        hasPhoto: !!data?.driver?.driver?.photo_url,
                        hasCar: !!data?.driver?.driver?.car,
                        hasDriverLocation: !!data?.driver?.location,
                    });

                    // Именно из-за этих двух случаев экран показывал пустые поля
                    if (data?.driver && !data?.driver?.driver) {
                        log.warn("У заказа есть водитель, но нет блока driver.driver — карточка будет неполной");
                    }
                    if (data?.status != null && !statusMap[data.status]) {
                        log.warn("Неизвестный статус заказа", { status: data.status });
                    }
                }

                setActiveOrderData(data);
                // Обновляем расчет времени при получении новых данных
                if (data?.driver?.location?.latitude && data?.start_location?.latitude) {
                    const start = [
                        (data.driver?.location?.latitude != null ? data.driver.location.latitude : null),
                        (data.driver?.location?.longitude != null ? data.driver.location.longitude : null)
                    ];
                    const end = [
                        data.start_location?.latitude || 0,
                        data.start_location?.longitude || 0
                    ];
                    calculateTravelTime(start, end);
                }
            })
            .catch((error) => {
                const code = error?.response?.status;
                if (code === 404) {
                    if (lastSignature.current !== "none") {
                        lastSignature.current = "none";
                        log.info("Активного заказа нет (404)");
                    }
                } else {
                    log.error("Не удалось получить активный заказ", { code, message: error?.message });
                }
                // If 404 — no active order, stop polling
                setActiveOrderData(null);
            });
    }

    useEffect(() => {
        updateActiveOrderData()
        const updateOrderDataInterval = setInterval(updateActiveOrderData, 5000)
        return () => {
            clearInterval(updateOrderDataInterval)
        }
    }, [])

    const flyTo = (coords, zoom = 17) => {
        map.flyTo([coords.latitude, coords.longitude], zoom, {
            duration: 0.5,
        });
    };

    // Guard: don't render map until order data is loaded
    const orderLoaded = !!activeOrderData;

    const displayMap = useMemo(() => {
        if (!orderLoaded) return null;
        return (
        <MapContainer
            center={driverLocation || centerPosition || [54.9, 52.3]}
            zoom={zoom}
            style={{ width: "100vw", height: "65vh" }}
            ref={setMap}
        >
            <ActiveOrderRouting />
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {driverLocation &&
                <Marker position={driverLocation} icon={driverLocationIcon}>
                    <Popup>
                        <div className="driver-popup">
                            {/* Имя лежит в driver.first_name, а не во вложенном driver.driver */}
                            <b>{textOf(activeOrderData?.driver?.first_name) || "Водитель"}</b>
                            {travelTime !== null && (
                                <div>Приедет через ~{travelTime} мин.</div>
                            )}
                        </div>
                    </Popup>
                </Marker>
            }
            {(activeOrderData?.status === 102 || activeOrderData?.status === 103) &&
                <CircleMarker
                    center={[activeOrderData?.start_location?.latitude || 0, activeOrderData?.start_location?.longitude || 0]}
                    pathOptions={{ color: "red" }}
                    radius={5}
                />
            }
            {activeOrderData?.status === 104 &&
                <CircleMarker
                    center={[activeOrderData?.end_location?.latitude || 0, activeOrderData?.end_location?.longitude || 0]}
                    pathOptions={{ color: "grey" }}
                    radius={5}
                />
            }
        </MapContainer>
    );
    }, [orderLoaded]);

    if (!activeOrderData) {
        return <Loader text={"Загрузка заказа..."} />;
    }

    if (activeOrderData?.status === 401 || activeOrderData?.status === 100 || activeOrderData?.status === 101) {
        return <Loader text={"Поиск водителей..."} />
    }

    // Данные могут прийти неполными — ни одно поле не рендерим «как есть»,
    // иначе на экране появляются пустые плашки и [object Object].
    const driverName = textOf(activeOrderData.driver?.first_name) || "Водитель";
    const car = driverInfo?.car;
    const carColor = textOf(car?.color);
    const carBrand = textOf(car?.brand);
    const carNumber = textOf(car?.number);
    const photoUrl = driverInfo?.photo_url;
    const showPhoto = !!photoUrl && !photoFailed;

    return activeOrderData?.driver ? (
        <>
            {displayMap}

            <Navbar
                isInput={true}
                minHeight={"40%"}
                maxHeight={"70%"}
                zIndex={1003}
                opened={activeOrderNavbarOpened}
                setOpened={setActiveOrderNavbarOpened}
            >
                <div className="active-order-menu__container">
                    <div className="active-order-menu__heading">
                        Вас повезёт
                    </div>

                    <div className="active-order-menu__flex-container">
                        {showPhoto ? (
                            <img
                                src={photoUrl}
                                alt={`Фото водителя: ${driverName}`}
                                className="active-order-menu__driver-photo"
                                onError={() => {
                                    log.warn("Фото водителя не загрузилось", { photoUrl });
                                    setPhotoFailed(true);
                                }}
                            />
                        ) : (
                            <div
                                className="active-order-menu__driver-photo active-order-menu__driver-photo--placeholder"
                                aria-hidden="true"
                            >
                                {driverName.charAt(0).toUpperCase()}
                            </div>
                        )}

                        <div className="active-order-menu__driver-info">
                            <div className="active-order-menu__driver-name">
                                {driverName}
                            </div>

                            {(carColor || carBrand) && (
                                <div className="active-order-menu__car-info">
                                    {carColor && <b>{carColor}</b>}
                                    {carBrand ? `${carColor ? " " : ""}${carBrand}` : ""}
                                </div>
                            )}
                        </div>
                    </div>

                    {carNumber && (
                        <div className="active-order-menu__number-plate">
                            {carNumber}
                        </div>
                    )}

                    <div className="active-order-menu__order-status">
                        {statusMap[activeOrderData.status] || STATUS_FALLBACK}
                        {travelTime !== null && activeOrderData.status === 102 && (
                            <div className="travel-time-info">
                                Приедет через ~{travelTime} мин.
                            </div>
                        )}
                    </div>
                </div>
            </Navbar>
        </>
    ) : null
}

export default ActiveOrderMenu;